"use client"

import dynamic from "next/dynamic"
import { usePathname } from "next/navigation"
import { useEffect, useLayoutEffect, useState } from "react"
import { getCookie, setCookie } from "@/shared/lib/cookies"
import { stripLocaleFromPath, supportedLocales } from "@/shared/lib/i18n/config"
import {
  buildAgeGateBootScript,
  setAgeGateState,
} from "@/shared/lib/age-gate-boot"

const AGE_VERIFIED_COOKIE = "accessAgeDisclaimer"
const AGE_VERIFIED_COOKIE_DAYS = 730
const AGE_VERIFICATION_EXEMPT_ROUTES = [
  "/legal",
  "/workbench",
  "/onboarding-preview",
]

const AGE_GATE_BOOT_SCRIPT = buildAgeGateBootScript({
  cookieName: AGE_VERIFIED_COOKIE,
  exemptRoutes: AGE_VERIFICATION_EXEMPT_ROUTES,
  locales: supportedLocales,
})

const AgeVerificationModal = dynamic(() =>
  import("@/shared/components/modals/age-verification-modal").then(
    (module) => module.AgeVerificationModal
  )
)

/**
 * Rendered first in <body> (PLE-452). An opaque shield plus an inline script
 * that runs before any page content paints: the shield stays up until the
 * visitor has confirmed 18+ on this device or the route is exempt. See
 * age-gate-boot.ts.
 */
export function AgeGateShield() {
  return (
    <>
      <script dangerouslySetInnerHTML={{ __html: AGE_GATE_BOOT_SCRIPT }} />
      <div className="age-gate-shield" aria-hidden="true" />
    </>
  )
}

/**
 * Keeps the animation and modal UI out of the initial bundle for verified
 * visitors and exempt content routes. First-time visitors still receive the
 * age gate immediately after the cookie/path check.
 */
export function AgeVerificationHost() {
  const pathname = usePathname()
  const [shouldLoadModal, setShouldLoadModal] = useState(false)

  // Before paint on every route change: raise or drop the shield so a
  // client-side move from an exempt page never shows gated content.
  useLayoutEffect(() => {
    const normalizedPathname = pathname ? stripLocaleFromPath(pathname) : pathname
    const isExemptRoute = AGE_VERIFICATION_EXEMPT_ROUTES.some(
      (route) =>
        normalizedPathname === route ||
        normalizedPathname?.startsWith(`${route}/`)
    )
    const needsGate = !isExemptRoute && !getCookie(AGE_VERIFIED_COOKIE)

    setAgeGateState(needsGate ? "required" : "ok")
    setShouldLoadModal(needsGate)
  }, [pathname])

  // Remembered per device: re-write the confirmation once per visit so
  // browsers that cap script-set cookies (Safari ITP, 7 days) keep it while
  // the visitor keeps coming back.
  useEffect(() => {
    if (getCookie(AGE_VERIFIED_COOKIE)) {
      setCookie(AGE_VERIFIED_COOKIE, "true", AGE_VERIFIED_COOKIE_DAYS)
    }
  }, [])

  return shouldLoadModal ? <AgeVerificationModal /> : null
}
